import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useRouter } from "@tanstack/react-router";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { RemotionPreview } from "@/components/preview/RemotionPreview";
import { PreviewAppearanceSection } from "@/components/preview/PreviewAppearanceSection";
import { CanvasFields } from "@/components/preview/CanvasFields";
import { SvgPreview } from "@/components/preview/SvgPreview";
import { PreviewModal } from "@/components/preview/PreviewModal";
import { applySvgFields } from "@/lib/svgFields";
import { embedSvgFonts } from "@/lib/svgFontEmbed";
import { Tabs, type Tab } from "@/components/ui/Tabs";
import { FieldEditor } from "./FieldEditor";
import { FieldSchemaEditor } from "./FieldSchemaEditor";
import { insertFieldIntoCode, removeFieldFromCode } from "@/lib/fieldCodeSync";
import { resolveDurationFrames } from "@/lib/durationResolve";
import { CodeEditor } from "@/components/editor/CodeEditor";
import { RenderButton } from "@/components/render/RenderButton";
import { RenderControls } from "@/components/render/RenderControls";
import { SvgSettingsPanel } from "@/components/render/SvgSettingsPanel";
import { DEFAULT_RENDER_SETTINGS, type RenderSettings } from "@/types/render";
import { TemplateActionsMenu } from "./TemplateActionsMenu";
import { RenderHistory } from "@/components/render/RenderHistory";
import { useRendersQuery } from "@/api/renders";
import { useRootPromptQuery } from "@/api/prompts";
import {
  useTemplateByIdQuery,
  useTemplateVersionsQuery,
  useToggleVisibilityMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useSetThumbnailMutation,
  useShareTemplateMutation,
  useUnshareTemplateMutation,
  useTemplateSharesQuery,
} from "@/api/templates";
import { toBlob as domToImageBlob } from "html-to-image";
import { apiFetch, apiUrl } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import type { FieldDefinition } from "@/types/template";

interface TemplateDetailViewProps {
  templateId: string;
}
// HMR nudge: header layout fix 2026-04-28

type TabId = "fields" | "code" | "settings" | "renders" | "history";

/**
 * Canned migration prompt seeded into the prompt input when the user clicks
 * "Upgrade Text Fields". The user reviews and sends — they can edit it first
 * if they want to scope the change. Wording is deliberately exhaustive so
 * Claude rewrites every read site instead of guessing which fields to touch.
 */
const UPGRADE_TEXT_FIELDS_PROMPT = `Upgrade this template to use the new "text-rich" field type for every text/textarea field.

KEY PRESERVATION (critical): keep every existing field key EXACTLY as-is. Do NOT rename, merge, split, or remove any field. If the schema currently has a field called \`scrambleText\`, the upgraded schema must still have a field called \`scrambleText\` — just with a different type/default shape.

For EACH field whose current \`type\` is "text" or "textarea":
1. Change \`type\` to "text-rich".
2. Replace its \`default\` value (a string) with an object: { "text": <original string>, "font": <pick a registered family that matches the design>, "fontSize": <reasonable px for that text's role: 96+ for hero titles, 56-72 for titles, 32-48 for subtitles, 22-32 for body> }.
3. In default_props (defaults.json), wrap the same key the same way — same key name, value is now the object.
4. Update component.tsx EVERY read site for that field. The prop value is no longer a string; it's an object with .text/.font/.fontSize. Find ALL of these patterns and convert:
   - \`{props.<key>}\` (JSX text)                      → \`{props.<key>.text}\`
   - \`props.<key>.length\`                            → \`props.<key>.text.length\`
   - \`props.<key>.split(...)\` / .charAt / .slice / .indexOf / .toUpperCase / .substring / any string method → \`props.<key>.text.split(...)\` etc.
   - \`\\\`\${props.<key>}\\\`\` (template literal)              → \`\\\`\${props.<key>.text}\\\`\`
   - Destructured: \`const { <key> } = props\` followed by \`<key>.length\` → either keep the destructure but read \`<key>.text.length\`, or rename the destructure: \`const { text: <key> } = props.<key>\`
   - Anywhere the prop is treated as a string (concatenation, comparison) → use \`.text\` accessor
5. On the JSX element that RENDERS that field as visible text, also set: \`style={{ ...existing, fontFamily: \\\`'\${props.<key>.font}', system-ui, sans-serif\\\`, fontSize: props.<key>.fontSize, fontWeight: props.<key>.bold ? 700 : 400, fontStyle: props.<key>.italic ? 'italic' : 'normal', textDecoration: props.<key>.underline ? 'underline' : 'none' }}\`. REMOVE any hardcoded fontFamily/fontSize/fontWeight/fontStyle/textDecoration that previously controlled that element so the user's panel choices win.

Self-check before returning: grep your output for \`props.<key>.length\`, \`props.<key>.split\`, \`props.<key>.charAt\`, etc. If you find any without \`.text\` between them, fix them.

Constraints:
- font value MUST be one of the registered families (Rubik, Inter, Roboto, Open Sans, Montserrat, Poppins, Lato, Raleway, Oswald, Bebas Neue, Geist, JetBrains Mono, Fira Code, IBM Plex Mono, Fragment Mono, Playfair Display, Merriweather). Pick what matches the existing design — if the original used a sans, stay sans; serif → serif.
- Do NOT change any animation logic, timing, layout, or non-text fields. Preserve them exactly.
- bold / italic / underline are OPTIONAL flat flags on the value (whole-text, not per-character). They default to undefined / false; templates apply them via the conditional style snippet above. Don't add them to default_props unless the original design called for that text to be bold/italic/underlined — if you do, set them as plain booleans like \`"bold": true\`. Don't try to wrap subsections in <b>/<i>/<u> tags — formatting applies to the entire string only.

Return the complete updated component.tsx, fields.json, and defaults.json.`;

export function TemplateDetailView({ templateId }: TemplateDetailViewProps) {
  const { data: template, isLoading } = useTemplateByIdQuery(templateId);
  // SVG templates render via SvgPreview (token substitution) and skip the
  // entire Remotion compile / player / render pipeline.
  const isSvg = template?.project_kind === "svg";
  const { data: versions } = useTemplateVersionsQuery(templateId);
  const [activeTab, setActiveTab] = useLocalStorage<TabId>(
    "motionforge-template-detail-tab",
    "fields",
  );
  const [editedCode, setEditedCode] = useState<string | null>(null);
  const [codeDirty, setCodeDirty] = useState(false);
  // Mirrors editedCode/codeDirty for field_schema. The schema editor was
  // previously mutating the React Query cache directly, which never persisted
  // because the save mutation only sent name + tsx_code. Now schema edits go
  // here and ride the same Save button, with the schema included in the
  // payload when dirty.
  const [editedSchema, setEditedSchema] = useState<FieldDefinition[] | null>(
    null,
  );
  const [schemaDirty, setSchemaDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldValues, setFieldValues] = useLocalStorage<Record<string, unknown>>(
    `motionforge-fields-${templateId}`,
    {},
  );
  // Read render settings so the preview reflects the user's chosen
  // canvas size (what-you-see-is-what-you-get for aspect ratio changes
  // like landscape → portrait).
  const [renderSettings] = useLocalStorage<{
    width?: number;
    height?: number;
    fps?: number;
    [k: string]: unknown;
  }>("motionforge-render-settings", {});
  const [moduleUrl, setModuleUrl] = useState<string | null>(null);
  const [compileError, setCompileError] = useState<string | null>(null);
  const [codeFullscreen, setCodeFullscreen] = useState(false);
  const [fieldsMode, setFieldsMode] = useState<"values" | "schema">("values");
  const [fieldsFullscreen, setFieldsFullscreen] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareUsername, setShareUsername] = useState("");
  const [shareError, setShareError] = useState<string | null>(null);
  const [previewMaximized, setPreviewMaximized] = useState(false);

  // Portal targets for RemotionPreview's settings toolbar and playback bar.
  // The unified edge-to-edge layout mounts the toolbar in the header's right
  // half and the playback bar in the bottom transport strip (mirrors
  // PromptWorkspace). useState (not useRef) so the portals attach once the
  // slot elements mount.
  const [previewToolbarEl, setPreviewToolbarEl] =
    useState<HTMLDivElement | null>(null);
  const [playbackBarEl, setPlaybackBarEl] = useState<HTMLDivElement | null>(
    null,
  );

  // Left/right split ratio (% width of the editor pane on lg+ screens).
  // Draggable via the divider, double-click resets to 50, persisted so the
  // detail view opens the way the user likes to work. Clamped 25–75.
  const [splitPct, setSplitPct] = useLocalStorage<number>(
    "motionforge-template-split",
    50,
  );
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const onDividerPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const container = splitContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const onMove = (ev: PointerEvent) => {
        const pct = ((ev.clientX - rect.left) / rect.width) * 100;
        setSplitPct(Math.min(75, Math.max(25, Math.round(pct * 10) / 10)));
      };
      const onUp = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [setSplitPct],
  );

  const currentUser = useAuthStore((s) => s.user);
  const isOwner = template != null && currentUser != null && template.user_id === currentUser.id;

  const router = useRouter();
  const visibilityMutation = useToggleVisibilityMutation(templateId);
  const updateMutation = useUpdateTemplateMutation(templateId);
  const deleteMutation = useDeleteTemplateMutation();
  const thumbnailMutation = useSetThumbnailMutation(templateId);

  // Ref on the wrapper around the Remotion Player. html-to-image
  // walks the DOM under this node to produce the PNG thumbnail.
  const previewCaptureRef = useRef<HTMLDivElement>(null);
  // Imperative handle to the Remotion Player exposed by RemotionPreview
  // via the onPlayerRef callback. We use this to seek past frame 0
  // (typically blank — intros fade in) before snapshotting the thumbnail.
  // `unknown` because PlayerRef's full type lives in @remotion/player and
  // we only need a couple of methods on it.
  const playerRef = useRef<{
    seekTo: (frame: number) => void;
    pause: () => void;
  } | null>(null);
  const shareMutation = useShareTemplateMutation(templateId);

  // Inline rename state. `null` means "not editing"; a string means
  // "showing the input pre-filled with this value." On submit we PATCH
  // and clear; on Esc we clear without submitting.
  const [editingName, setEditingName] = useState<string | null>(null);

  /**
   * Capture the Remotion Player container as a PNG and POST it as the
   * template's thumbnail. Used by both the manual Set Thumbnail button
   * and the auto-capture effect.
   *
   * Returns the resulting blob (or null on failure) so the auto-capture
   * effect can decide whether to retry or mark as attempted.
   */
  // Refs keep captureThumbnail's deps array small and avoid a forward-
  // reference issue where currentValues / template are declared further
  // down the function body. The refs are written by an effect a few lines
  // below and always reflect the latest render's values when capture
  // actually runs.
  const captureCtxRef = useRef<{
    template: typeof template;
    currentValues: Record<string, unknown>;
  }>({ template: undefined, currentValues: {} });

  const captureThumbnail = useCallback(async (): Promise<Blob | null> => {
    const node = previewCaptureRef.current;
    if (!node) return null;

    // Wait up to 3s for the Player ref to attach. Without this guard,
    // auto-capture fires before RemotionPreview's useEffect has bubbled
    // the ref up — so we'd seek nothing and grab a frame-0 black square.
    const refDeadline = Date.now() + 3000;
    while (!playerRef.current && Date.now() < refDeadline) {
      await new Promise((r) => setTimeout(r, 100));
    }

    // Seek the Player to ~30% of the duration before snapshotting. Frame
    // 0 is almost always blank (intros fade in / slide from off-screen),
    // so capturing at frame 0 produces a useless black thumbnail. ~30%
    // typically lands after the reveal animation has resolved but well
    // before any outro fade-out begins.
    const ctx = captureCtxRef.current;
    const player = playerRef.current;
    const fps =
      typeof ctx.currentValues.fps === "number" && (ctx.currentValues.fps as number) > 0
        ? (ctx.currentValues.fps as number)
        : ctx.template?.fps || 30;
    const duration = ctx.template
      ? resolveDurationFrames(ctx.currentValues, fps, ctx.template.duration_frames, ctx.template.field_schema)
      : 0;
    if (player && duration > 0) {
      const targetFrame = Math.max(1, Math.floor(duration * 0.3));
      try {
        player.pause();
        player.seekTo(targetFrame);
      } catch {
        // Ignore — capture whatever frame is currently rendered.
      }
      // Wait long enough for Remotion to commit the seeked frame to the
      // DOM. requestAnimationFrame alone isn't enough — Remotion's
      // composition rerenders trail the seek by one or two paints. 250ms
      // is a generous margin that covers slow clients without making the
      // capture feel laggy.
      await new Promise((r) => setTimeout(r, 250));
    }

    try {
      const blob = await domToImageBlob(node, {
        canvasWidth: 1280,
        canvasHeight: 720,
        cacheBust: true,
      });
      if (!blob) return null;
      await thumbnailMutation.mutateAsync(blob);
      return blob;
    } catch (e) {
      console.error("[thumbnail] capture failed:", e);
      return null;
    }
  }, [thumbnailMutation]);
  const unshareMutation = useUnshareTemplateMutation(templateId);
  const { data: shares } = useTemplateSharesQuery(templateId);
  const { data: templateRenders } = useRendersQuery({ template_id: templateId });
  // Fetch the original prompt that started this template's session.
  // template.description points at whatever the LATEST iteration prompt
  // was — usually a refinement like "make it snappier". The header
  // should show what the user originally asked for.
  const { data: rootPrompt } = useRootPromptQuery(template?.prompt_id);

  // Parse field_schema -- handle both array and already-parsed forms
  const fields = useMemo<FieldDefinition[]>(() => {
    // editedSchema (local staged edits) wins over the server-side schema
    // until saved or discarded — same pattern as editedCode.
    if (editedSchema) return editedSchema;
    if (!template?.field_schema) return [];
    const fs = template.field_schema;
    if (Array.isArray(fs)) return fs as FieldDefinition[];
    if (typeof fs === "string") {
      try { return JSON.parse(fs) as FieldDefinition[]; } catch { return []; }
    }
    return [];
  }, [template?.field_schema, editedSchema]);

  // Initialize field values from template defaults
  const initialValues = useMemo(() => {
    const defaults: Record<string, unknown> = {};
    for (const field of fields) {
      defaults[field.key] = field.default;
    }
    // The template's saved working values (default_props — what the user
    // saw when they hit Save as Template) beat bare schema defaults.
    // Without this the detail page silently reverts every customization
    // the save captured. Unknown keys are dropped so a stale prop can't
    // outlive a schema edit.
    const saved = template?.default_props;
    if (saved && typeof saved === "object") {
      for (const [key, value] of Object.entries(saved)) {
        if (fields.some((f) => f.key === key)) {
          defaults[key] = value;
        }
      }
    }
    return defaults;
  }, [fields, template?.default_props]);

  const currentValues = { ...initialValues, ...fieldValues };

  // Keep the capture context ref in sync each render so captureThumbnail
  // (whose deps don't include these values) always sees the latest.
  captureCtxRef.current = { template, currentValues };

  const lastCompileHashRef = useRef<string | null>(null);
  async function compileAndPreview(tsx: string) {
    // Don't clear moduleUrl — keep the old preview visible while the new
    // one compiles. Clearing it would unmount the Remotion Player on every
    // keystroke (even with debounce), resetting playback to frame 0 and
    // flashing a loading state. The old preview stays on screen until the
    // new module URL replaces it seamlessly.
    //
    // Preserve window.scrollY around the moduleUrl swap. Without this,
    // browser scroll-anchoring sometimes pulls the page back toward the
    // top when the Player resizes / reflows, which is infuriating during
    // active code editing.
    const snapshotScrollY = typeof window !== "undefined" ? window.scrollY : 0;
    try {
      const result = await apiFetch<{ module_url: string }>("/preview/compile", {
        method: "POST",
        body: JSON.stringify({ tsx_code: tsx }),
      });
      const baseUrl = apiUrl(result.module_url);
      // Only update moduleUrl when the content hash actually changed.
      // The backend returns a content-hashed filename (e.g. /modules/abc123.js).
      // If the hash is the same, keeping the old URL avoids remounting the
      // Player and resetting playback to frame 0. Only add a cache-buster
      // when the hash differs OR when recovering from an error state.
      const newHash = result.module_url;
      if (newHash !== lastCompileHashRef.current || compileError) {
        lastCompileHashRef.current = newHash;
        setModuleUrl(baseUrl + `?t=${Date.now()}`);
        if (typeof window !== "undefined" && snapshotScrollY > 0) {
          // Two RAFs to outlast the Player's lazy mount + layout shift,
          // then a 200ms timeout as a final safety net for slow devices.
          const restore = () => {
            if (Math.abs(window.scrollY - snapshotScrollY) > 1) {
              window.scrollTo({ top: snapshotScrollY, behavior: "auto" });
            }
          };
          requestAnimationFrame(() => {
            requestAnimationFrame(restore);
          });
          setTimeout(restore, 200);
        }
      }
      setCompileError(null);
    } catch (err) {
      setCompileError(err instanceof Error ? err.message : "Compile failed");
    }
  }

  // Live preview: debounce recompile when code is edited (500ms delay)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!editedCode || activeTab !== "code" || isSvg) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void compileAndPreview(editedCode);
    }, 1000);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [editedCode, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape exits fields fullscreen
  useEffect(() => {
    if (!fieldsFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); setFieldsFullscreen(false); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [fieldsFullscreen]);

  useEffect(() => {
    if (!fieldsFullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [fieldsFullscreen]);

  // Ctrl+` toggles fullscreen code editor from any tab
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "`") {
        e.preventDefault();
        setCodeFullscreen((v) => {
          if (!v) setActiveTab("code");
          return !v;
        });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [setActiveTab]);

  // Compile TSX when template loads. SVG templates preview from source —
  // never touch the Remotion compile endpoint (it would fail on XML).
  useEffect(() => {
    if (!template?.tsx_code || isSvg) return;

    let cancelled = false;

    async function compile() {
      try {
        const result = await apiFetch<{ module_url: string }>("/preview/compile", {
          method: "POST",
          body: JSON.stringify({ tsx_code: template!.tsx_code }),
        });
        if (!cancelled) {
          setModuleUrl(apiUrl(result.module_url) + `?t=${Date.now()}`);
          setCompileError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setCompileError(err instanceof Error ? err.message : "Compile failed");
        }
      }
    }

    void compile();
    return () => { cancelled = true; };
  }, [template?.tsx_code, isSvg]);

  // Migrate the old persisted "render" tab id (from before this tab was
  // renamed "Settings") so a returning user doesn't land on a dead tab that
  // no longer exists in the tabs list.
  useEffect(() => {
    if ((activeTab as string) === "render") setActiveTab("settings");
  }, [activeTab, setActiveTab]);

  // If fields tab is active but there are no fields, fall back to code
  useEffect(() => {
    if (activeTab === "fields" && fields.length === 0 && template) {
      setActiveTab("code");
    }
  }, [activeTab, fields.length, template, setActiveTab]);

  // Auto-capture thumbnail once per session for owner-only templates
  // that have no thumbnail yet. We probe the existing thumbnail.png with
  // a HEAD request — a 404 means "no thumbnail, capture one." Only fires
  // after the preview has compiled (moduleUrl is set) and after a small
  // delay so the Player has time to render its first frame. The session-
  // storage flag keeps repeat visits from re-uploading the same image.
  useEffect(() => {
    // SVG cards render the substituted SVG directly — no PNG thumbnail.
    if (!isOwner || !moduleUrl || !template || isSvg) return;
    const flagKey = `mf-thumb-attempted:${template.id}`;
    if (sessionStorage.getItem(flagKey)) return;

    let cancelled = false;
    const handle = setTimeout(async () => {
      if (cancelled) return;
      try {
        // Probe whether a thumbnail already exists on disk.
        const probeUrl = apiUrl(`/templates/${template.id}/thumbnail.png`);
        const probe = await fetch(probeUrl, { method: "HEAD" });
        if (probe.ok) {
          sessionStorage.setItem(flagKey, "exists");
          return;
        }
      } catch {
        // Network failure — abort, don't poison the session flag so we
        // can retry next visit.
        return;
      }
      // No thumbnail yet — capture one.
      sessionStorage.setItem(flagKey, "attempted");
      void captureThumbnail();
    }, 1500);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [isOwner, moduleUrl, template, captureThumbnail, isSvg]);

  function handleFieldChange(key: string, value: unknown) {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  }

  function handleShare() {
    if (!shareUsername.trim()) return;
    setShareError(null);
    shareMutation.mutate(shareUsername.trim(), {
      onSuccess: () => {
        setShareUsername("");
      },
      onError: (err) => {
        setShareError(err instanceof Error ? err.message : "Failed to share");
      },
    });
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent/30 border-t-accent" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="py-16 text-center text-sm text-ink-2">
        Template not found.
      </div>
    );
  }

  const rendersCount = templateRenders?.length ?? 0;
  const historyCount = versions?.length ?? 0;

  // SVG source with the current field values substituted — what the preview
  // shows and what Download saves. Cheap for SVG; skip for Remotion TSX.
  const svgSource = editedCode ?? template.tsx_code;
  const substitutedSvg = isSvg
    ? applySvgFields(svgSource, currentValues)
    : "";
  const svgSlug =
    template.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "graphic";
  const downloadSvg = async () => {
    const slug = svgSlug;
    const embedded = await embedSvgFonts(substitutedSvg);
    const url = URL.createObjectURL(
      new Blob([embedded], { type: "image/svg+xml" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug}.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const tabs: Tab[] = [
    {
      id: "fields",
      label: "Fields",
      badge: fields.length > 0 ? fields.length : undefined,
      disabled: fields.length === 0,
    },
    { id: "code", label: "Code" },
    // Settings tab consolidates canvas natives, preview appearance, and render
    // output. SVG → viewBox readout + PNG export panel; Remotion → canvas
    // (width/height/fps) + preview appearance (BG/scale) + render controls.
    { id: "settings", label: "Settings" },
    // SVG templates have no renders — the Render pipeline is Remotion-only.
    ...(isSvg
      ? []
      : [
          {
            id: "renders" as const,
            label: "Renders",
            badge: rendersCount > 0 ? rendersCount : undefined,
          },
        ]),
    {
      id: "history",
      label: "History",
      badge: historyCount > 0 ? historyCount : undefined,
    },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Metadata header — a compact shrink-0 bar above the unified card.
          Title/rename + badges on the left, the kebab actions menu inline
          at the right. Kept slim so the workspace gets the vertical space. */}
      <div className="flex shrink-0 items-center gap-3 border-b border-line px-4 py-2">
        <div className="min-w-0 flex-1">
          {editingName !== null ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const next = editingName.trim();
                if (!next || next === template.name) {
                  setEditingName(null);
                  return;
                }
                updateMutation.mutate(
                  { name: next },
                  { onSettled: () => setEditingName(null) },
                );
              }}
            >
              <input
                autoFocus
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onBlur={() => setEditingName(null)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setEditingName(null);
                  }
                }}
                className="w-full truncate rounded-lg border border-line-strong bg-surface px-2 py-0.5 text-lg font-semibold text-ink focus:border-line-strong focus:outline-none"
              />
            </form>
          ) : (
            <div className="flex min-w-0 items-center gap-2">
              <h1
                className={`min-w-0 truncate text-lg font-semibold text-ink ${
                  isOwner ? "cursor-text rounded px-1 -mx-1 hover:bg-surface-3" : ""
                }`}
                onClick={() => {
                  if (isOwner) setEditingName(template.name);
                }}
                title={isOwner ? "Click to rename" : undefined}
              >
                {template.name}
              </h1>
              <span className="shrink-0 rounded bg-surface-3 px-2 py-0.5 text-xs text-ink-2">
                {template.category}
              </span>
              <span className="shrink-0 text-xs text-ink-3">
                v{template.version}
              </span>
              <span className="shrink-0 text-xs text-ink-3">
                {template.width}x{template.height} @ {template.fps}fps
              </span>
              {template.is_public ? (
                <span className="shrink-0 rounded bg-accent-soft px-2 py-0.5 text-xs text-accent">
                  Public
                </span>
              ) : (
                <span className="shrink-0 rounded bg-surface-3 px-2 py-0.5 text-xs text-ink-2">
                  Private
                </span>
              )}
            </div>
          )}
          {(rootPrompt?.prompt_text || template.description) && (
            <div className="mt-0.5 flex items-center gap-2">
              <p
                className="min-w-0 flex-1 truncate text-xs text-ink-3"
                title={rootPrompt?.prompt_text || template.description || ""}
              >
                {rootPrompt?.prompt_text || template.description}
              </p>
              <CopyPromptButton
                text={rootPrompt?.prompt_text || template.description || ""}
              />
            </div>
          )}
        </div>
        {/* Single overflow ⋯ menu replaces the previous wall of buttons.
            Render stays primary and lives in the preview toolbar via the
            `toolbarRight` slot below. Sibling of the title column so it
            sits inline at the right of the title row. */}
        <div className="shrink-0">
          <TemplateActionsMenu
            isOwner={isOwner}
            isSvg={isSvg}
            isPublic={template.is_public}
            visibilityPending={visibilityMutation.isPending}
            deletePending={deleteMutation.isPending}
            setThumbnailPending={thumbnailMutation.isPending}
            setThumbnailDisabled={!moduleUrl}
            upgradeAvailable={fields.some(
              (f) => f.type === "text" || f.type === "textarea",
            )}
            onToggleVisibility={() =>
              visibilityMutation.mutate(!template.is_public)
            }
            onShare={() => setShowShareDialog(!showShareDialog)}
            onSetThumbnail={() => {
              void captureThumbnail();
            }}
            onEditWithAI={() => {
              sessionStorage.setItem(
                "motionforge-edit-template",
                JSON.stringify({
                  templateId: template.id,
                  name: template.name,
                  tsxCode: template.tsx_code,
                  fieldSchema: fields,
                  defaultProps: template.default_props,
                  projectKind: template.project_kind,
                }),
              );
              void router.navigate({ to: "/create" });
            }}
            onUpgradeTextFields={() => {
              sessionStorage.setItem(
                "motionforge-edit-template",
                JSON.stringify({
                  templateId: template.id,
                  name: template.name,
                  tsxCode: template.tsx_code,
                  fieldSchema: fields,
                  defaultProps: template.default_props,
                  projectKind: template.project_kind,
                  pendingDraft: UPGRADE_TEXT_FIELDS_PROMPT,
                }),
              );
              void router.navigate({ to: "/create" });
            }}
            onExport={async () => {
              const res = await fetch(
                apiUrl(`/templates/${template.id}/export`),
                {
                  headers: {
                    Authorization: `Bearer ${useAuthStore.getState().token}`,
                  },
                },
              );
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${template.name.toLowerCase().replace(/\s+/g, "-")}.motionforge.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            onDelete={() => {
              const ok = window.confirm(
                `Delete "${template.name}"? This removes the template from your dashboard. Renders already produced are preserved.`,
              );
              if (!ok) return;
              deleteMutation.mutate(template.id, {
                onSuccess: () => {
                  void router.navigate({ to: "/" });
                },
              });
            }}
          />
        </div>
      </div>

      {/* Share Dialog — a shrink-0 strip between the metadata header and the
          unified card when open. */}
      {showShareDialog && isOwner && (
        <div className="shrink-0 border-b border-line bg-surface-2 px-4 py-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-ink-2">
              Share Template
            </span>
            <button
              onClick={() => setShowShareDialog(false)}
              className="text-xs text-ink-3 hover:text-ink"
            >
              Close
            </button>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={shareUsername}
              onChange={(e) => setShareUsername(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleShare()}
              placeholder="Enter username..."
              className="flex-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
            />
            <button
              onClick={handleShare}
              disabled={shareMutation.isPending || !shareUsername.trim()}
              className="rounded-lg bg-accent px-4 py-1.5 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
            >
              {shareMutation.isPending ? "..." : "Add"}
            </button>
          </div>
          {shareError && (
            <p className="mt-2 text-xs text-danger">{shareError}</p>
          )}

          {shares && shares.length > 0 && (
            <div className="mt-3 divide-y divide-line">
              {shares.map((share) => (
                <div
                  key={share.user_id}
                  className="flex items-center justify-between py-1.5"
                >
                  <span className="text-sm text-ink">
                    {share.username}
                  </span>
                  <button
                    onClick={() => unshareMutation.mutate(share.user_id)}
                    disabled={unshareMutation.isPending}
                    className="text-xs text-danger transition-colors hover:underline disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          {shares && shares.length === 0 && (
            <p className="mt-2 text-xs text-ink-3">
              Not shared with anyone yet.
            </p>
          )}
        </div>
      )}

      {/* Unified edge-to-edge card — one borderless surface filling the
          content area (no rounded corners, no outer border). The header bar
          spans the top: tabs + code save/discard on the left of the split
          line, the "Live preview" label + portaled preview toolbar on the
          right. The body holds the two resizable panes. Mirrors
          PromptWorkspace's layout exactly. */}
      <div
        style={{ "--split": `${splitPct}%` } as React.CSSProperties}
        className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-2"
      >
        {/* Header bar. */}
        <div className="flex shrink-0 items-stretch border-b border-line">
          <div className="flex min-w-0 flex-1 items-center lg:w-[var(--split)] lg:flex-none">
            <Tabs
              className="w-full !border-b-0"
              tabs={tabs}
              activeId={activeTab}
              onChange={(id) => setActiveTab(id as TabId)}
              rightSlot={
                activeTab === "code" ? (
                  <div className="flex items-center gap-3 py-1">
                    {(codeDirty || schemaDirty) && isOwner && (
                      <button
                        onClick={async () => {
                          setSaving(true);
                          try {
                            const payload: {
                              name: string;
                              tsx_code?: string;
                              field_schema?: FieldDefinition[];
                            } = { name: template.name };
                            if (codeDirty && editedCode) {
                              payload.tsx_code = editedCode;
                            }
                            if (schemaDirty && editedSchema) {
                              payload.field_schema = editedSchema;
                            }
                            await updateMutation.mutateAsync(payload);
                            setCodeDirty(false);
                            setSchemaDirty(false);
                            // SVG previews from source (SvgPreview reads
                            // editedCode via substitutedSvg) — never compile.
                            if (editedCode && !isSvg) {
                              void compileAndPreview(editedCode);
                            }
                          } finally {
                            setSaving(false);
                          }
                        }}
                        disabled={saving}
                        className="rounded-lg bg-accent px-3 py-1 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
                      >
                        {saving
                          ? "Saving..."
                          : codeDirty && schemaDirty
                            ? "Save Code + Schema"
                            : codeDirty
                              ? "Save Code"
                              : "Save Schema"}
                      </button>
                    )}
                    {(codeDirty || schemaDirty) && (
                      <button
                        onClick={() => {
                          setEditedCode(null);
                          setCodeDirty(false);
                          setEditedSchema(null);
                          setSchemaDirty(false);
                        }}
                        className="text-xs text-ink-2 hover:text-ink"
                      >
                        Discard
                      </button>
                    )}
                  </div>
                ) : undefined
              }
            />
          </div>
          <div className="hidden w-[5px] shrink-0 lg:block" />
          <div className="hidden min-w-0 flex-1 items-center gap-3 px-3 lg:flex">
            <span className="shrink-0 text-sm font-medium text-ink-2">
              {isSvg ? "Preview" : "Live preview"}
            </span>
            {isSvg ? (
              // SVG: no render toolbar, just a Download (substituted SVG).
              <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5">
                <button
                  onClick={downloadSvg}
                  className="shrink-0 rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
                >
                  Download
                </button>
              </div>
            ) : (
              // RemotionPreview portals its settings toolbar here — including
              // the primary Render button via `toolbarRight`. Empty until a
              // module is loaded.
              <div
                ref={setPreviewToolbarEl}
                className="flex min-w-0 flex-1 items-center justify-end"
              />
            )}
          </div>
        </div>

        {/* Body — the two panes, split by the drag line. */}
        <div
          ref={splitContainerRef}
          className="flex min-h-0 flex-1 flex-col lg:flex-row"
        >
          {/* Left pane — the tab content (Fields / Code / Renders / History).
              Code hands the full pane height to Monaco (which scrolls
              internally); the other tabs scroll within the pane. */}
          <div className="order-1 flex min-h-0 min-w-0 flex-1 flex-col lg:w-[var(--split)] lg:flex-none">
            <div
              className={
                activeTab === "code"
                  ? "flex min-h-0 flex-1 flex-col p-3"
                  : "min-h-0 flex-1 overflow-y-auto p-3"
              }
            >
          {activeTab === "fields" && (() => {
            const content = (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 rounded-md bg-surface-3 p-0.5">
                    <button
                      type="button"
                      onClick={() => setFieldsMode("values")}
                      className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                        fieldsMode === "values"
                          ? "bg-accent-soft text-accent"
                          : "text-ink-2 hover:text-ink"
                      }`}
                    >
                      Values
                    </button>
                    <button
                      type="button"
                      onClick={() => setFieldsMode("schema")}
                      className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                        fieldsMode === "schema"
                          ? "bg-accent-soft text-accent"
                          : "text-ink-2 hover:text-ink"
                      }`}
                    >
                      Edit Schema
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFieldsFullscreen((v) => !v)}
                    className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2 py-1 text-xs font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
                  >
                    {fieldsFullscreen ? "Exit" : "Fullscreen"}
                  </button>
                </div>
                {fieldsMode === "values" ? (
                  fields.length > 0 ? (
                    <FieldEditor
                      fields={fields}
                      values={currentValues}
                      onChange={handleFieldChange}
                    />
                  ) : (
                    <div className="py-8 text-center text-xs text-ink-3">
                      No fields. Switch to "Edit Schema" to add some.
                    </div>
                  )
                ) : (
                  <FieldSchemaEditor
                    fields={editedSchema ?? fields}
                    projectKind={isSvg ? "svg" : "remotion"}
                    onChange={(updated) => {
                      const sourceFields = editedSchema ?? fields;
                      const oldKeys = new Set(
                        sourceFields.map(
                          (f: import("@/types/template").FieldDefinition) =>
                            f.key,
                        ),
                      );
                      const newKeys = new Set(updated.map((f) => f.key));
                      // SVG: `{{key}}` tokens are placed by hand in the source
                      // — the TSX-oriented code-sync would corrupt the XML, so
                      // only seed default values for new keys.
                      if (isSvg) {
                        for (const f of updated) {
                          if (!oldKeys.has(f.key)) handleFieldChange(f.key, f.default);
                        }
                        setEditedSchema(updated);
                        setSchemaDirty(true);
                        return;
                      }
                      let code = editedCode ?? template.tsx_code;
                      for (const f of updated) {
                        if (!oldKeys.has(f.key)) {
                          code = insertFieldIntoCode(code, f.key, f.type);
                          handleFieldChange(f.key, f.default);
                        }
                      }
                      for (const f of sourceFields) {
                        if (!newKeys.has(f.key)) {
                          code = removeFieldFromCode(code, f.key);
                        }
                      }
                      if (code !== (editedCode ?? template.tsx_code)) {
                        setEditedCode(code);
                        setCodeDirty(true);
                      }
                      // Stage the schema in local state — saved when the user
                      // clicks Save. No more direct cache mutation.
                      setEditedSchema(updated);
                      setSchemaDirty(true);
                    }}
                  />
                )}
              </div>
            );

            if (fieldsFullscreen) {
              return (
                <div className="fixed inset-0 z-50 flex flex-col bg-surface">
                  <div className="flex-1 overflow-y-auto p-4">{content}</div>
                  <div className="border-t border-line px-4 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => setFieldsFullscreen(false)}
                      className="text-xs text-ink-2 hover:text-ink"
                    >
                      Press Esc or click to exit fullscreen
                    </button>
                  </div>
                </div>
              );
            }
            return content;
          })()}
          {activeTab === "code" && (
            <CodeEditor
              value={editedCode ?? template.tsx_code}
              onChange={(val) => {
                setEditedCode(val);
                setCodeDirty(val !== template.tsx_code);
              }}
              language={isSvg ? "xml" : "tsx"}
              isFullscreen={codeFullscreen}
              onToggleFullscreen={() => setCodeFullscreen((v) => !v)}
              // SVG uses hand-placed `{{key}}` tokens — no Insert Field button.
              onInsertField={
                isSvg
                  ? undefined
                  : (field, updatedCode) => {
                      // Stage schema + code; user clicks Save to persist both.
                      setEditedSchema([...(editedSchema ?? fields), field]);
                      setSchemaDirty(true);
                      setEditedCode(updatedCode);
                      setCodeDirty(true);
                      handleFieldChange(field.key, field.default);
                    }
              }
            />
          )}
          {activeTab === "settings" &&
            (isSvg ? (
              <SvgSettingsPanel svg={substitutedSvg} fileBase={svgSlug} />
            ) : (
              <div className="flex flex-col gap-8">
                {/* (a) Canvas — the composition natives (width/height/fps).
                    Saving PUTs them onto the template; the preview reads
                    template.* for its base dimensions, so an invalidation
                    flows straight through. */}
                <CanvasSettingsSection
                  templateId={template.id}
                  name={template.name}
                  description={template.description}
                  category={template.category}
                  width={template.width || 1920}
                  height={template.height || 1080}
                  fps={template.fps || 30}
                />
                {/* (b) Preview appearance — relocated from the preview
                    toolbar. Same localStorage slots, so preview + render
                    stay in lockstep. */}
                <PreviewAppearanceSection />
                {/* (c) Render output — the existing inline render controls. */}
                <section className="flex flex-col gap-3">
                  <h3 className="text-sm font-semibold text-ink">
                    Render output
                  </h3>
                  <RenderSettingsTab
                    derivedDurationFrames={resolveDurationFrames(
                      currentValues,
                      typeof currentValues.fps === "number" &&
                        currentValues.fps > 0
                        ? currentValues.fps
                        : template.fps || 30,
                      template.duration_frames,
                      template.field_schema,
                    )}
                    fps={template.fps || 30}
                    width={template.width || 1920}
                    height={template.height || 1080}
                  />
                </section>
              </div>
            ))}
          {activeTab === "renders" && (
            <RenderHistory
              jobs={(templateRenders ?? []).map((r) => ({
                ...r,
                template_name: template.name,
              }))}
            />
          )}
          {activeTab === "history" && (
            <div className="divide-y divide-line">
              {versions && versions.length > 0 ? (
                versions.map((version) => (
                  <div
                    key={version.id}
                    className="flex items-center justify-between py-2"
                  >
                    <span className="text-sm text-ink">
                      v{version.version}
                    </span>
                    <span className="text-xs text-ink-3">
                      {new Date(version.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))
              ) : (
                <p className="py-4 text-xs text-ink-3">
                  No previous versions.
                </p>
              )}
            </div>
          )}
            </div>
          </div>

          {/* Drag line — desktop only. Double-click resets to 50/50. */}
          <div
            role="separator"
            aria-orientation="vertical"
            title="Drag to resize · double-click to reset"
            onPointerDown={onDividerPointerDown}
            onDoubleClick={() => setSplitPct(50)}
            className="group relative order-2 hidden w-[5px] shrink-0 cursor-col-resize touch-none lg:block"
          >
            <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-line transition-colors group-hover:bg-accent group-active:bg-accent" />
          </div>

          {/* Right pane — live preview stage. previewCaptureRef wraps the
              stage for html-to-image thumbnail capture; RemotionPreview
              portals its toolbar + playback bar out into the header + bottom
              strip. */}
          <div className="relative order-3 flex min-h-0 min-w-0 flex-1 flex-col border-t border-line lg:border-t-0">
            <div
              ref={previewCaptureRef}
              className="flex min-h-[280px] min-w-0 flex-1 overflow-hidden p-3"
            >
              {isSvg ? (
                <SvgPreview code={substitutedSvg} />
              ) : moduleUrl ? (() => {
                // Same-aspect upscale (e.g. 1080p → 4K): keep the Player at
                // the template's native dimensions so hardcoded pixel values
                // render at the intended size and are uniformly scaled by the
                // stage fit math. Aspect change (landscape → portrait): use
                // the target dimensions so the user sees the real crop.
                // Mirrors frontend/scripts/render.ts so preview matches render.
                const baseWidth = template.width || 1920;
                const baseHeight = template.height || 1080;
                const targetWidth = renderSettings.width || baseWidth;
                const targetHeight = renderSettings.height || baseHeight;
                const baseAspect = baseWidth / baseHeight;
                const targetAspect = targetWidth / targetHeight;
                const sameAspect = Math.abs(baseAspect - targetAspect) < 0.01;
                const previewWidth = sameAspect ? baseWidth : targetWidth;
                const previewHeight = sameAspect ? baseHeight : targetHeight;
                return (
                  <RemotionPreview
                    fill
                    toolbarZoomOnly
                    toolbarPortal={previewToolbarEl}
                    playbackPortal={playbackBarEl}
                    moduleUrl={moduleUrl}
                    inputProps={currentValues}
                    durationFrames={
                      resolveDurationFrames(currentValues, typeof currentValues.fps === "number" && currentValues.fps > 0 ? currentValues.fps : template.fps || 30, template.duration_frames, template.field_schema)
                    }
                    fps={
                      typeof currentValues.fps === "number" && currentValues.fps > 0
                        ? currentValues.fps
                        : template.fps || 30
                    }
                    width={previewWidth}
                    height={previewHeight}
                    baseWidth={baseWidth}
                    baseHeight={baseHeight}
                    onPlayerRef={(p) => {
                      playerRef.current = p as typeof playerRef.current;
                    }}
                    toolbarRight={
                      <RenderButton
                        templateId={template.id}
                        inputProps={currentValues}
                        hideSettingsPill
                        initialSettings={{
                          duration_frames: resolveDurationFrames(
                            currentValues,
                            typeof currentValues.fps === "number" &&
                              currentValues.fps > 0
                              ? currentValues.fps
                              : template.fps || 30,
                            template.duration_frames,
                            template.field_schema,
                          ),
                          fps: template.fps || 30,
                          width: template.width || 1920,
                          height: template.height || 1080,
                        }}
                      />
                    }
                  />
                );
              })() : compileError ? (
                <div className="m-auto flex items-center justify-center text-xs text-warning">
                  Preview unavailable: {compileError}
                </div>
              ) : (
                <div className="m-auto flex items-center justify-center gap-3">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-accent/30 border-t-accent" />
                  <span className="text-sm text-ink-2">
                    Compiling preview...
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom transport strip — the playback bar spans the whole work
          area (both panes). RemotionPreview portals the transport controls
          (incl. the player-fullscreen button) into the slot; kept thin.
          SVG has no player, so the strip is omitted. */}
      {!isSvg && (
        <div className="flex min-h-[30px] shrink-0 items-center border-t border-line bg-surface-2 px-2 py-0.5">
          <div
            ref={setPlaybackBarEl}
            className="flex min-w-0 flex-1 items-center"
          />
        </div>
      )}

      {!isSvg && (() => {
        // Same aspect-match branch as the inline preview above. Render uses
        // supersample (scale=N) for same-aspect upscale, so keeping the Player
        // at the template's native dimensions makes preview match render.
        const baseWidth = template.width || 1920;
        const baseHeight = template.height || 1080;
        const targetWidth = renderSettings.width || baseWidth;
        const targetHeight = renderSettings.height || baseHeight;
        const baseAspect = baseWidth / baseHeight;
        const targetAspect = targetWidth / targetHeight;
        const sameAspect = Math.abs(baseAspect - targetAspect) < 0.01;
        const previewWidth = sameAspect ? baseWidth : targetWidth;
        const previewHeight = sameAspect ? baseHeight : targetHeight;
        return (
          <PreviewModal
            open={previewMaximized}
            onClose={() => setPreviewMaximized(false)}
            moduleUrl={moduleUrl}
            inputProps={currentValues}
            durationFrames={
              typeof currentValues.durationFrames === "number" && currentValues.durationFrames > 0
                ? currentValues.durationFrames
                : template.duration_frames || 150
            }
            fps={
              typeof currentValues.fps === "number" && currentValues.fps > 0
                ? currentValues.fps
                : template.fps || 30
            }
            width={previewWidth}
            height={previewHeight}
            baseWidth={baseWidth}
            baseHeight={baseHeight}
          />
        );
      })()}
    </div>
  );
}

/**
 * Canvas section of the Settings tab — the composition natives (width,
 * height, fps). These are the template's real dimensions; the preview reads
 * template.width/height/fps for its base size, so persisting them here and
 * invalidating the template query flows straight into the preview.
 *
 * Owns its own local edit state + mutation so all its hooks sit below
 * TemplateDetail's early returns. Local inputs mirror the persisted values
 * via an effect (the async-prop pattern): after a save the template refetches
 * with new dimensions and the inputs re-seed, clearing the dirty state.
 */
function CanvasSettingsSection({
  templateId,
  name,
  description,
  category,
  width,
  height,
  fps,
}: {
  templateId: string;
  name: string;
  description: string;
  category: string;
  width: number;
  height: number;
  fps: number;
}) {
  const update = useUpdateTemplateMutation(templateId);
  const [w, setW] = useState<number>(width);
  const [h, setH] = useState<number>(height);
  const [f, setF] = useState<number>(fps);

  // Re-seed local inputs when the persisted values change (e.g. after a save
  // invalidates + refetches the template). useState only honors its initial
  // value once, so late-arriving props must be mirrored explicitly.
  useEffect(() => {
    setW(width);
    setH(height);
    setF(fps);
  }, [width, height, fps]);

  const dirty = w !== width || h !== height || f !== fps;

  const save = () => {
    // Clamp to the backend's accepted ranges (width/height 16..8192, fps
    // 1..120) so the local inputs and the persisted values agree even if the
    // server clamps. Include name/description/category — the update endpoint
    // takes a partial patch, but sending the current metadata keeps the row's
    // other editable fields explicit alongside the dimension change.
    update.mutate({
      name,
      description,
      category,
      width: Math.max(16, Math.min(8192, Math.round(w) || width)),
      height: Math.max(16, Math.min(8192, Math.round(h) || height)),
      fps: Math.max(1, Math.min(120, Math.round(f) || fps)),
    });
  };

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-ink">Canvas</h3>
      <p className="text-[11px] text-ink-3">
        The composition's native size and frame rate. Changing these updates
        the live preview.
      </p>
      <CanvasFields
        width={w}
        height={h}
        fps={f}
        onWidth={setW}
        onHeight={setH}
        onFps={setF}
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || update.isPending}
          className="rounded-lg bg-accent px-3 py-1 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
        >
          {update.isPending ? "Saving…" : "Save canvas"}
        </button>
        {update.isError && (
          <span className="text-xs text-danger">
            {update.error instanceof Error
              ? update.error.message
              : "Save failed"}
          </span>
        )}
      </div>
    </section>
  );
}

/**
 * Inline Render-settings panel for the "Settings" tab (Remotion templates).
 * Owns the "motionforge-render-settings" localStorage slot — the SAME slot
 * the preview toolbar's RenderButton reads — so the tab and the Render button
 * always agree. Duration in Auto mode follows the live value derived from the
 * template's timing fields, mirroring RenderButton/RenderControls exactly.
 *
 * Owning the hook here (rather than in TemplateDetail's body) keeps it below
 * TemplateDetail's isLoading/not-found early returns without changing that
 * component's hook count.
 */
function RenderSettingsTab({
  derivedDurationFrames,
  fps,
  width,
  height,
}: {
  derivedDurationFrames: number;
  fps: number;
  width: number;
  height: number;
}) {
  const [storedSettings, setSettings] = useLocalStorage<RenderSettings>(
    "motionforge-render-settings",
    {
      ...DEFAULT_RENDER_SETTINGS,
      fps,
      width,
      height,
      duration_frames:
        derivedDurationFrames > 0
          ? derivedDurationFrames
          : DEFAULT_RENDER_SETTINGS.duration_frames,
    },
  );
  // Auto mode (the default) follows the duration derived from timing fields
  // instead of the persisted global blob — same rule RenderButton applies,
  // so the tab and the actual render always agree.
  const settings: RenderSettings =
    storedSettings.duration_auto !== false &&
    derivedDurationFrames &&
    derivedDurationFrames > 0
      ? { ...storedSettings, duration_frames: derivedDurationFrames }
      : storedSettings;

  return <RenderControls settings={settings} onChange={setSettings} />;
}

/**
 * Tiny copy button placed at the end of the prompt description. Uses the
 * Clipboard API; falls back to a no-op + console warn on legacy browsers
 * where navigator.clipboard isn't available (rare but safer than
 * exploding). Flips to ✓ for ~1.4s on success so the user knows it
 * worked without spawning a toast.
 */
function CopyPromptButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Legacy fallback for non-secure contexts. Creates a temp
        // textarea, selects, execCommand('copy'), removes.
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (err) {
      console.warn("[CopyPromptButton] clipboard write failed:", err);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      title={copied ? "Copied!" : "Copy prompt"}
      aria-label="Copy prompt"
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border text-xs transition-colors ${
        copied
          ? "border-accent text-accent"
          : "border-line bg-surface text-ink-2 hover:border-line-strong hover:text-ink"
      }`}
    >
      {copied ? "✓" : "⧉"}
    </button>
  );
}
